import type {ApolloClient} from '@apollo/client';
import type {MutableRefObject} from 'react';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import useRecentActivityPolling from 'nav/sidepanel/home/useRecentActivityPolling';
import notifications from 'notifications';
import * as logging from 'util/Logging';
import {getUserMessage} from 'util/Net';

import {InAppNotification, InAppNotificationStatus, NotificationTab} from './InAppNotificationConstants';
import {dismissNotification, markNotificationRead, markNotificationsRead} from './InAppNotificationDAO';
import {
  IN_APP_NOTIFICATION_FRAGMENT,
  IN_APP_NOTIFICATION_LISTING_QUERY,
  IN_APP_NOTIFICATION_UNREAD_SUMMARY_QUERY,
  InAppNotificationListingQueryData,
  InAppNotificationListingQueryVariables,
  InAppNotificationUnreadSummaryQueryData,
} from './InAppNotificationQueries';

const LISTING_PAGE_SIZE = 50;

// The statuses each tab both queries from the server and displays. Each tab has its own listing query
// for this set, so unread rows are never crowded out of the Unread tab by a window full of reads, and
// dismissed rows are never fetched. The client-side filter re-applies the same set to the loaded rows:
// a write flips a row's status in the cache locally, but the cached list still holds that id until a
// refetch, so the filter drops reads/dismissals out of the view immediately without a round-trip.
const STATUSES_BY_TAB: Record<NotificationTab, Array<InAppNotificationStatus>> = {
  [NotificationTab.ALL]: [InAppNotificationStatus.UNREAD, InAppNotificationStatus.READ],
  [NotificationTab.UNREAD]: [InAppNotificationStatus.UNREAD],
};

// Stable reference for `useMemo`/effect deps.
const EMPTY_NOTIFICATIONS: Array<InAppNotification> = [];

/** Flip a notification's `status` in the Apollo cache in place, without a network refetch. */
function setNotificationStatusInCache(
  client: ApolloClient<object>,
  id: string,
  status: InAppNotificationStatus
): void {
  const cacheId = client.cache.identify({__typename: 'InAppNotificationNode', id});
  if (cacheId == null) {
    return;
  }
  client.cache.modify({
    id: cacheId,
    fields: {
      status: () => status,
    },
  });
}

/** Read a notification's current cached `status`, or null if it is not in the cache. */
function readNotificationStatusFromCache(
  client: ApolloClient<object>,
  id: string
): InAppNotificationStatus | null {
  const cacheId = client.cache.identify({__typename: 'InAppNotificationNode', id});
  if (cacheId == null) {
    return null;
  }
  const node = client.readFragment<{status: InAppNotificationStatus}>({
    id: cacheId,
    fragment: IN_APP_NOTIFICATION_FRAGMENT,
  });
  return node?.status ?? null;
}

/**
 * Optimistically adjust the cached unread-summary count so the badge tracks a read/dismiss in lockstep
 * with the list, instead of lagging until the next summary poll.
 */
function adjustUnreadSummaryCount(client: ApolloClient<object>, delta: number): void {
  if (delta === 0) {
    return;
  }
  const existing = client.readQuery<InAppNotificationUnreadSummaryQueryData>({
    query: IN_APP_NOTIFICATION_UNREAD_SUMMARY_QUERY,
  });
  const summary = existing?.inAppNotificationUnreadSummary;
  if (summary == null) {
    return;
  }
  client.writeQuery<InAppNotificationUnreadSummaryQueryData>({
    query: IN_APP_NOTIFICATION_UNREAD_SUMMARY_QUERY,
    data: {
      inAppNotificationUnreadSummary: {
        ...summary,
        unreadCount: Math.max(0, summary.unreadCount + delta),
      },
    },
  });
}

interface NotificationWrites {
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
}

/**
 * The inbox's write actions (mark read, mark all read, dismiss). Each dispatches its request and, on
 * success, applies the result to the Apollo cache instead of refetching the listing, so the list
 * converges on server truth immediately.
 *
 * @param loadedItems The rows currently loaded on the active tab; `markAllRead` marks the unread ones
 *   among these, i.e. the pages fetched so far, never unread rows the user has not paged to.
 */
function useNotificationWrites(
  client: ApolloClient<object>,
  loadedItems: Array<InAppNotification>
): NotificationWrites {
  const runUpdate = useCallback(
    (action: () => Promise<unknown>, applyToCache: () => void): Promise<void> =>
      action()
        .then(applyToCache)
        .catch((e: unknown) => {
          notifications.error(getUserMessage(e) ?? 'Could not update notification.');
        }),
    []
  );

  const markRead = useCallback(
    (id: string) =>
      runUpdate(
        () => markNotificationRead(id),
        () => {
          // Only an unread row moves the badge; re-reading a row already marked read is a no-op.
          const wasUnread = readNotificationStatusFromCache(client, id) === InAppNotificationStatus.UNREAD;
          setNotificationStatusInCache(client, id, InAppNotificationStatus.READ);
          adjustUnreadSummaryCount(client, wasUnread ? -1 : 0);
        }
      ),
    [runUpdate, client]
  );

  const markAllRead = useCallback(() => {
    // Only affects the unread rows loaded on the active tab, i.e. all pages fetched so far.
    const unreadIds = loadedItems.filter((n) => n.status === InAppNotificationStatus.UNREAD).map((n) => n.id);
    if (unreadIds.length === 0) {
      return Promise.resolve();
    }
    return runUpdate(
      () => markNotificationsRead(unreadIds),
      () => {
        unreadIds.forEach((id) => {
          setNotificationStatusInCache(client, id, InAppNotificationStatus.READ);
        });
        // unreadIds are all currently-unread rows, so the badge drops by exactly that many; any
        // unread rows the user has not paged to are untouched and the next poll restores them.
        adjustUnreadSummaryCount(client, -unreadIds.length);
      }
    );
  }, [loadedItems, runUpdate, client]);

  const dismiss = useCallback(
    (id: string) =>
      runUpdate(
        () => dismissNotification(id),
        () => {
          // Dismissing an already-read row leaves the unread count alone; only an unread row decrements it.
          const wasUnread = readNotificationStatusFromCache(client, id) === InAppNotificationStatus.UNREAD;
          setNotificationStatusInCache(client, id, InAppNotificationStatus.DISMISSED);
          adjustUnreadSummaryCount(client, wasUnread ? -1 : 0);
        }
      ),
    [runUpdate, client]
  );

  return {markRead, markAllRead, dismiss};
}

export interface UnreadSummary {
  unreadCount: number;
  latestEventAt: string | null;
}

export interface UseInAppNotificationsResult {
  unreadCount: number;
  notifications: Array<InAppNotification>;
  isLoading: boolean;
  isError: boolean;
  // True once the active tab has more pages to fetch (`nextToken` is non-empty).
  hasMore: boolean;
  // Fetches and appends the active tab's next page. A no-op while a page is already in flight or when
  // there are no more pages.
  loadMore: () => void;
  // True when the summary reports an event newer than the active tab's newest loaded row, i.e. a new
  // notification has arrived since the list was last (re)loaded. Drives the panel's "New
  // notifications" pill; clicking it calls `refreshHead`.
  hasNewNotifications: boolean;
  // Refreshes the active tab's listing from the head. Interim behavior is a full refetch of the tab's
  // pages; the head-prepend follow-up swaps this body to prepend new rows onto the accumulated tail.
  refreshHead: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

interface PagedNotificationListing {
  items: Array<InAppNotification>;
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refreshHead: () => Promise<void>;
  // Newest loaded event timestamp for this tab (listing is newest-first), or null when nothing is
  // loaded. A ref so callers can compare against the summary without re-running on every array change.
  newestLoadedEventAtRef: MutableRefObject<string | null>;
}

/**
 * One tab's listing subscription: a cursor-paged `inAppNotificationListing` query for a fixed status
 * set, lazily activated (skipped until first opened, then kept subscribed cache-first) and paged by
 * appending `nextToken` pages to the loaded list. Called once per tab so each tab owns its own token
 * and accumulated pages, which is what makes per-tab pagination and scroll-depth retention natural.
 *
 * `refreshHead` is the forward-compat seam: its interim body is a full refetch; the head-prepend
 * follow-up rewrites only this body to prepend new arrivals while preserving the accumulated tail.
 */
function usePagedNotificationListing({
  statuses,
  skip,
}: {
  statuses: Array<InAppNotificationStatus>;
  skip: boolean;
}): PagedNotificationListing {
  // Guards `loadMore` against overlapping fetches (a fast double-scroll before the page resolves).
  const isFetchingMoreRef = useRef(false);

  const listingResult = useSkippableBenchlingQuery<
    InAppNotificationListingQueryData,
    InAppNotificationListingQueryVariables
  >(IN_APP_NOTIFICATION_LISTING_QUERY, {
    skip,
    variables: {pageSize: LISTING_PAGE_SIZE, statuses},
    fetchPolicy: 'cache-first',
  });

  const isLoaded = listingResult.type === QueryResultType.SUCCESS;
  // Retain the last loaded page while a `fetchMore` is in flight to keep scroll position steady.
  const lastLoadedDataRef = useRef<InAppNotificationListingQueryData | null>(null);
  if (isLoaded) {
    lastLoadedDataRef.current = listingResult.data;
  }
  const loadedData = isLoaded ? listingResult.data : lastLoadedDataRef.current;

  const items = loadedData?.inAppNotificationListing.items ?? EMPTY_NOTIFICATIONS;
  // The server returns an empty cursor once there are no more pages.
  const nextToken = loadedData?.inAppNotificationListing.nextToken ?? '';
  const hasMore = nextToken !== '';

  // Only updated while loaded, so a transient ERROR keeps the last known timestamp instead of
  // resetting to null.
  const newestLoadedEventAtRef = useRef<string | null>(null);
  if (isLoaded) {
    newestLoadedEventAtRef.current = items.length > 0 ? items[0].lastEventAt : null;
  }

  const {fetchMore, refetch} = listingResult;

  const loadMore = useCallback(() => {
    if (nextToken === '' || isFetchingMoreRef.current) {
      return;
    }
    isFetchingMoreRef.current = true;
    // `nextToken` is an opaque cursor: pass it back verbatim and never parse or reconstruct it, so a
    // future backend token-shape change stays invisible to this code.
    fetchMore({
      variables: {pageSize: LISTING_PAGE_SIZE, nextToken, statuses},
      // Append the next page's items to the ones already loaded and adopt its cursor, rather than
      // replacing the accumulated list.
      updateQuery: (previous, {fetchMoreResult}) => {
        if (fetchMoreResult == null) {
          return previous;
        }
        return {
          ...fetchMoreResult,
          inAppNotificationListing: {
            ...fetchMoreResult.inAppNotificationListing,
            items: [
              ...previous.inAppNotificationListing.items,
              ...fetchMoreResult.inAppNotificationListing.items,
            ],
          },
        };
      },
    })
      .catch((e: unknown) => {
        // A failed page also surfaces through the query result's error state on the next render, so this
        // stays silent for the user; log to Sentry so the failure is still observable.
        logging.error(e instanceof Error ? e : new Error('Failed to load more in-app notifications'));
      })
      .finally(() => {
        isFetchingMoreRef.current = false;
      });
  }, [fetchMore, nextToken, statuses]);

  const refreshHead = useCallback(async () => {
    // Interim behavior: refetch this tab's pages from the head. The head-prepend follow-up rewrites
    // only this body to prepend new arrivals onto the accumulated tail.
    await refetch();
  }, [refetch]);

  return {
    items,
    // Only the initial load (nothing retained yet) shows the panel's loading state. Once a page has
    // loaded, the transient PENDING of a `fetchMore`/refetch keeps rendering the retained rows above
    // rather than flashing the full-panel throbber, which would unmount the list and reset its scroll.
    isLoading: listingResult.type === QueryResultType.PENDING && lastLoadedDataRef.current == null,
    isError: listingResult.type === QueryResultType.ERROR,
    hasMore,
    loadMore,
    refreshHead,
    newestLoadedEventAtRef,
  };
}

/**
 * Data layer for the notification inbox. Polls the lightweight unread summary for the badge and backs
 * each tab with its own cursor-paged listing query, lazily activated the first time that tab is
 * opened. When the summary reports an event newer than the active tab's newest loaded row it surfaces
 * `hasNewNotifications` rather than refetching, so accumulated pages and scroll depth are preserved
 * and the refresh happens only when the user clicks the panel's "New notifications" pill
 * (`refreshHead`). The pill only shows on the tab the user is actively looking at; two cases reconcile
 * the head silently instead: an arrival while the open tab is empty (no pill to float over an empty
 * list and no accumulated context to preserve), and an arrival waiting on a tab the user is not
 * currently looking at (the panel is closed, or another tab is active) — reopening or switching back
 * pulls it in from the head rather than flashing the pill for a list the user has not seen since the
 * arrival. Writes update the Apollo cache
 * on success instead of refetching. `refetch` is for explicit consumer-driven refreshes and is not
 * called automatically after a write.
 *
 * @param activeTab Which tab's notifications to load.
 * @param isPanelOpen When false, skips the heavier listing queries so the closed panel only pays for
 *   the summary poll until it is first opened.
 */
export default function useInAppNotifications(
  activeTab: NotificationTab,
  {isPanelOpen}: {isPanelOpen: boolean}
): UseInAppNotificationsResult {
  const summaryResult = useBenchlingQuery<InAppNotificationUnreadSummaryQueryData>(
    IN_APP_NOTIFICATION_UNREAD_SUMMARY_QUERY,
    {fetchPolicy: 'cache-and-network'}
  );
  const {client} = summaryResult;

  // The badge should always stay current, so poll unconditionally (hasInProgress: true) and let the
  // shared hook pause polling whenever the tab is backgrounded.
  // How often to refresh the unread-count badge is config-driven so it can be tuned per tenant without a deploy.
  useRecentActivityPolling({
    hasInProgress: true,
    pollIntervalMs: BenchlingConfig.IN_APP_NOTIFICATION_UNREAD_SUMMARY_POLL_INTERVAL_MS,
    startPolling: summaryResult.startPolling,
    stopPolling: summaryResult.stopPolling,
    refetch: summaryResult.refetch,
  });

  // Activate each tab's listing the first time that tab is opened; afterwards keep it subscribed with
  // a cache-first policy so revisiting the tab reads from the cache instead of hitting the network
  // again. A tab stays skipped until it has been the active tab while the panel is open at least once.
  const hasOpenedTabRef = useRef<Record<NotificationTab, boolean>>({
    [NotificationTab.ALL]: false,
    [NotificationTab.UNREAD]: false,
  });
  if (isPanelOpen) {
    hasOpenedTabRef.current[activeTab] = true;
  }

  const allListing = usePagedNotificationListing({
    statuses: STATUSES_BY_TAB[NotificationTab.ALL],
    skip: !hasOpenedTabRef.current[NotificationTab.ALL],
  });
  const unreadListing = usePagedNotificationListing({
    statuses: STATUSES_BY_TAB[NotificationTab.UNREAD],
    skip: !hasOpenedTabRef.current[NotificationTab.UNREAD],
  });
  const activeListing = activeTab === NotificationTab.UNREAD ? unreadListing : allListing;

  // Keep the last successful summary so a transient poll failure (which flips the query result to
  // ERROR even while Apollo still holds the prior data) does not blank the badge to 0 or reset the
  // latest-event timestamp. On error we keep rendering the last known values until the next good
  // poll reconciles them.
  const lastSummaryRef = useRef<UnreadSummary>({unreadCount: 0, latestEventAt: null});
  if (summaryResult.type === QueryResultType.SUCCESS) {
    const summary = summaryResult.data.inAppNotificationUnreadSummary;
    lastSummaryRef.current = {unreadCount: summary.unreadCount, latestEventAt: summary.latestEventAt};
  }
  const {unreadCount} = lastSummaryRef.current;

  // A failing badge poll stays silent for the user (the last-good values above keep rendering), so log
  // to Sentry to keep the failure observable.
  const summaryResultType = summaryResult.type;
  const summaryError = summaryResult.type === QueryResultType.ERROR ? summaryResult.error : null;
  const wasSummaryErroringRef = useRef(false);
  useEffect(() => {
    if (summaryResultType === QueryResultType.ERROR) {
      if (!wasSummaryErroringRef.current) {
        wasSummaryErroringRef.current = true;
        logging.error(summaryError ?? new Error('In-app notification unread-summary poll failed'));
      }
    } else if (summaryResultType === QueryResultType.SUCCESS) {
      wasSummaryErroringRef.current = false;
    }
  }, [summaryResultType, summaryError]);

  // Newest event timestamp among the user's UNREAD notifications; kept current by the poll, so an
  // advance past the active tab's newest loaded row signals a newly arrived notification.
  const summaryLatestEventAt = lastSummaryRef.current.latestEventAt;

  // Filter the loaded rows to the active tab's statuses so a locally read or dismissed row (whose
  // cache status changed without a refetch) drops out of the view immediately.
  const activeItems = activeListing.items;
  const notificationItems = useMemo(
    () => activeItems.filter((n) => STATUSES_BY_TAB[activeTab].includes(n.status)),
    [activeItems, activeTab]
  );

  // A newer notification exists when the summary reports an event newer than the active tab's newest loaded row
  const newestLoadedEventAt = activeListing.newestLoadedEventAtRef.current;
  const hasNewerArrival =
    summaryLatestEventAt != null &&
    (newestLoadedEventAt == null || summaryLatestEventAt > newestLoadedEventAt);

  // The "New notifications" pill is only shown on the tab the user is actively looking at.
  // So each render flags every tab that is not the one being viewed yet.
  const deferredReconcileByTabRef = useRef<Record<NotificationTab, boolean>>({
    [NotificationTab.ALL]: false,
    [NotificationTab.UNREAD]: false,
  });
  const allNewestLoadedEventAtRef = allListing.newestLoadedEventAtRef;
  const unreadNewestLoadedEventAtRef = unreadListing.newestLoadedEventAtRef;
  useEffect(() => {
    const viewedTab = isPanelOpen ? activeTab : null;
    const loadedByTab: Record<NotificationTab, string | null> = {
      [NotificationTab.ALL]: allNewestLoadedEventAtRef.current,
      [NotificationTab.UNREAD]: unreadNewestLoadedEventAtRef.current,
    };
    (Object.keys(loadedByTab) as Array<NotificationTab>).forEach((tab) => {
      const loaded = loadedByTab[tab];
      const tabHasNewerArrival =
        summaryLatestEventAt != null && loaded != null && summaryLatestEventAt > loaded;
      if (tab !== viewedTab && tabHasNewerArrival) {
        deferredReconcileByTabRef.current[tab] = true;
      }
    });
  }, [summaryLatestEventAt, isPanelOpen, activeTab, allNewestLoadedEventAtRef, unreadNewestLoadedEventAtRef]);

  // Suppress the pill the instant a deferred reconcile is pending for the active tab, so the silent head
  // refresh below lands without the pill flashing first.
  const hasNewNotifications = hasNewerArrival && !deferredReconcileByTabRef.current[activeTab];

  const summaryRefetch = summaryResult.refetch;
  const activeRefreshHead = activeListing.refreshHead;
  const isActiveTabOpened = hasOpenedTabRef.current[activeTab];

  // Silently reconcile the active tab's head with a newer arrival in the two cases where the pill is
  // deliberately withheld: (1) the tab is showing an empty list, which has no rows to float the pill
  // over and no accumulated pages or scroll depth to preserve; and (2) the tab's head fell behind while
  // it was not the one being viewed (`deferredReconcileByTabRef`)
  const {isLoading: activeIsLoading, isError: activeIsError} = activeListing;
  const autoRefreshedEventAtByTabRef = useRef<Record<NotificationTab, string | null>>({
    [NotificationTab.ALL]: null,
    [NotificationTab.UNREAD]: null,
  });
  const autoRefreshInFlightByTabRef = useRef<Record<NotificationTab, boolean>>({
    [NotificationTab.ALL]: false,
    [NotificationTab.UNREAD]: false,
  });
  useEffect(() => {
    const tabReady = isPanelOpen && !activeIsLoading && !activeIsError;
    const shouldReconcileSilently =
      notificationItems.length === 0 || deferredReconcileByTabRef.current[activeTab];
    const alreadyHandlingArrival =
      autoRefreshInFlightByTabRef.current[activeTab] ||
      autoRefreshedEventAtByTabRef.current[activeTab] === summaryLatestEventAt;
    if (!tabReady || !hasNewerArrival || !shouldReconcileSilently || alreadyHandlingArrival) {
      return;
    }
    const refreshedTab = activeTab;
    const refreshedEventAt = summaryLatestEventAt;
    autoRefreshInFlightByTabRef.current[refreshedTab] = true;
    activeRefreshHead()
      .then(() => {
        // Mark the event handled only after the refresh lands, so a refresh that returns still-empty
        // does not re-fire for the same timestamp, and clear the deferred flag now that the head is
        // level again.
        autoRefreshedEventAtByTabRef.current[refreshedTab] = refreshedEventAt;
        deferredReconcileByTabRef.current[refreshedTab] = false;
      })
      .catch((e: unknown) => {
        // A failed reconcile leaves the prior view in place and the event un-handled; a later
        // qualifying render re-arms this refresh. It stays silent for the user (the listing's own
        // error state covers the UI), so log to Sentry to keep the failure observable.
        logging.error(e instanceof Error ? e : new Error('Failed to auto-refresh in-app notifications'));
      })
      .finally(() => {
        autoRefreshInFlightByTabRef.current[refreshedTab] = false;
      });
  }, [
    isPanelOpen,
    activeTab,
    activeIsLoading,
    activeIsError,
    notificationItems.length,
    hasNewerArrival,
    summaryLatestEventAt,
    activeRefreshHead,
  ]);

  const refetch = useCallback(async () => {
    // Only refresh the active tab's listing if it has actually been opened; refetching a still-skipped
    // query would fire a network request for a tab the user has never viewed.
    await Promise.all([summaryRefetch(), isActiveTabOpened ? activeRefreshHead() : Promise.resolve()]);
  }, [summaryRefetch, activeRefreshHead, isActiveTabOpened]);

  const {markRead, markAllRead, dismiss} = useNotificationWrites(client, notificationItems);

  const {isLoading, isError, hasMore, loadMore} = activeListing;

  return useMemo(
    () => ({
      unreadCount,
      notifications: notificationItems,
      isLoading,
      isError,
      hasMore,
      loadMore,
      hasNewNotifications,
      refreshHead: activeRefreshHead,
      markRead,
      markAllRead,
      dismiss,
      refetch,
    }),
    [
      unreadCount,
      notificationItems,
      isLoading,
      isError,
      hasMore,
      loadMore,
      hasNewNotifications,
      activeRefreshHead,
      markRead,
      markAllRead,
      dismiss,
      refetch,
    ]
  );
}
